import React, { useEffect, useState } from 'react';
import useServicesService from '../../services/useServicesService';
import { ServiceHistory, ServiceHistoryResponse } from '../../models/ServiceHistoryModel';
import './styles/ServiceHistoryTable.css';

interface ServiceHistoryTableProps {
  serviceId: number;
  /** Cuando true, no muestra el título (p. ej. dentro de pestañas). */
  embedded?: boolean;
}

const ServiceHistoryTable: React.FC<ServiceHistoryTableProps> = ({ serviceId, embedded }) => {
  const { getHistory, fetching } = useServicesService();
  const [history, setHistory] = useState<ServiceHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadHistory();
  }, [serviceId]);

  const loadHistory = () => {
    setLoading(true);
    setError(null);
    
    getHistory(serviceId, {
      onSuccess: (response: any) => {
        const data: ServiceHistoryResponse = response?.data || response;
        setHistory(data.history || []);
        setLoading(false);
      },
      onError: (err: any) => {
        const errorMsg = err?.message || err?.data?.message || 'Error al cargar el historial';
        setError(errorMsg);
        setLoading(false);
      },
    });
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('es-CL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(date);
    } catch {
      return dateString;
    }
  };

  const getChangeTypeIcon = (changeType: string) => {
    switch (changeType) {
      case 'STATUS_CHANGE':
        return '🔄';
      case 'ASSIGNMENT':
        return '👤';
      case 'FIELD_UPDATE':
        return '✏️';
      case 'SCHEDULE_CHANGE':
        return '🕒';
      case 'CREATION':
        return '➕';
      case 'DUPLICATION':
        return '📋';
      case 'DELETION':
        return '🗑️';
      default:
        return '📝';
    }
  };

  const getChangeTypeColor = (changeType: string) => {
    switch (changeType) {
      case 'STATUS_CHANGE':
        return 'status-change';
      case 'ASSIGNMENT':
        return 'assignment';
      case 'FIELD_UPDATE':
        return 'field-update';
      case 'SCHEDULE_CHANGE':
        return 'field-update';
      case 'CREATION':
        return 'creation';
      case 'DUPLICATION':
        return 'duplication';
      case 'DELETION':
        return 'deletion';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="service-history-container">
        <div className="history-loading">
          <div className="spinner"></div>
          <p>Cargando historial...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="service-history-container">
        <div className="history-error">
          <p>❌ {error}</p>
          <button onClick={loadHistory} className="btn-retry">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="service-history-container">
        <div className="history-empty">
          <p>📋 No hay registros de cambios para este servicio.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`service-history-container ${embedded ? 'history-embedded' : ''}`}>
      <div className="history-header">
        {!embedded && (
          <h3 className="history-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="currentColor"/>
            </svg>
            Historial de Cambios
          </h3>
        )}
        <span className="history-count">{history.length} cambios registrados</span>
      </div>

      <div className="history-table-wrapper">
        <table className="history-table">
          <thead>
            <tr>
              <th>Fecha y Hora</th>
              <th>Tipo de Cambio</th>
              <th>Usuario</th>
              <th>Descripción</th>
              <th>Valor Anterior</th>
              <th>Valor Nuevo</th>
            </tr>
          </thead>
          <tbody>
            {history.map((item) => (
              <tr key={item.id} className={`history-row ${getChangeTypeColor(item.change_type)}`}>
                <td className="history-date" data-label="Fecha y Hora">
                  {formatDate(item.created_at)}
                </td>
                <td className="history-type" data-label="Tipo">
                  <span className="type-badge">
                    {getChangeTypeIcon(item.change_type)} {item.change_type_label}
                  </span>
                </td>
                <td className="history-user" data-label="Usuario">
                  {item.user_full_name || `${item.user_first_name} ${item.user_last_name}`.trim() || 'Sistema'}
                </td>
                <td className="history-description" data-label="Descripción">
                  {item.change_description}
                </td>
                <td className="history-old-value" data-label="Valor Anterior">
                  {item.old_value && String(item.old_value).trim() ? item.old_value : '—'}
                </td>
                <td className="history-new-value" data-label="Valor Nuevo">
                  {item.new_value && String(item.new_value).trim() ? item.new_value : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServiceHistoryTable;

